/**
 * The client-side check every multi-file signed-page upload runs before anything is sent (the API
 * checks again and merges the files into one PDF): PDF, JPG or PNG, up to 10 files, 20 MB in
 * total. Each flow keeps its own wording for the refusals; the iPhone HEIC advice is shared.
 */
export const ALLOWED_UPLOAD_TYPES = ['application/pdf', 'image/jpeg', 'image/png'] as const
export const MAX_UPLOAD_FILES = 10
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024

const HEIC = /\.(heic|heif)$/i

/** A flow's own refusal messages, each saying how to fix the choice. */
export interface UploadProblemWords {
  unsupported: (fileName: string) => string
  /** Present when the flow refuses an empty file. */
  empty?: (fileName: string) => string
  tooMany: (count: number) => string
  tooLarge: string
}

/** Why the chosen files cannot be sent, with how to fix it; `null` when they are fine. */
export function uploadProblem(files: File[], words: UploadProblemWords): string | null {
  const heic = files.find((file) => HEIC.test(file.name) || /image\/hei[cf]/.test(file.type))
  if (heic) {
    return `${heic.name} is an iPhone HEIC photo. Export it as JPG or PDF (or set Camera > Formats to Most Compatible) and choose it again.`
  }
  const unsupported = files.find((file) => !(ALLOWED_UPLOAD_TYPES as readonly string[]).includes(file.type))
  if (unsupported) return words.unsupported(unsupported.name)
  const empty = files.find((file) => file.size === 0)
  if (empty && words.empty) return words.empty(empty.name)
  if (files.length > MAX_UPLOAD_FILES) return words.tooMany(files.length)
  if (files.reduce((sum, file) => sum + file.size, 0) > MAX_UPLOAD_BYTES) return words.tooLarge
  return null
}
